import React from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Flex, Link, Button } from 'nallian-shared-ui/lib/components';
import HasSomeRights from 'nallian-shared-ui/lib/layout/rights/HasSomeRights';
import { Table, Params, Actions, Menu } from 'nallian-table-builder-ui';
import NewUserGroup from './NewUserGroup';
import EditUserGroup from './EditUserGroup';
import { USER_GROUPS_RESOURCE, USER_GROUP_ACTIONS } from '../../constants';
import apiRoutes from '../../../../app/api/routes';
import UserGroupMembers from './UserGroupMembers';

const USER_GROUP_READ_RIGHTS = [
  'user-groups/read/non-system-organisation',
  'user-groups/read/system-organisation',
  'user-groups/read/own-organisation',
];

const USER_GROUP_CREATE_RIGHTS = [
  'user-groups/create/non-system-organisation',
  'user-groups/create/system-organisation',
  'user-groups/create/own-organisation',
];

const USER_GROUP_UPDATE_RIGHTS = [
  'user-groups/update/non-system-organisation',
  'user-groups/update/system-organisation',
  'user-groups/update/own-organisation',
];

const USER_GROUP_DELETE_RIGHTS = [
  'user-groups/delete/non-system-organisation',
  'user-groups/delete/system-organisation',
  'user-groups/delete/own-organisation',
];

const UserGroupsOverview = ({ forbidManageBecauseSystemOrg, organisationId, urlRoot }) => {
  const { t } = useTranslation();
  const { action, actionId } = useParams();

  return (
    <>
      <HasSomeRights rights={USER_GROUP_CREATE_RIGHTS}>
        <>
          <Flex>
            <Link to={`${urlRoot}/user-groups/${USER_GROUP_ACTIONS.NEW}`}>
              <Button btnprimary icon="add" content={t('addUserGroup')} />
            </Link>
          </Flex>
          <NewUserGroup
            urlRoot={urlRoot}
            organisationId={organisationId}
            open={organisationId && action === USER_GROUP_ACTIONS.NEW}
          />
        </>
      </HasSomeRights>
      <HasSomeRights rights={USER_GROUP_UPDATE_RIGHTS}>
        <EditUserGroup
          urlRoot={urlRoot}
          organisationId={organisationId}
          open={organisationId && actionId && action === USER_GROUP_ACTIONS.EDIT}
        />
      </HasSomeRights>
      <HasSomeRights rights={USER_GROUP_READ_RIGHTS}>
        <UserGroupMembers
          urlRoot={urlRoot}
          organisationId={organisationId}
          open={organisationId && actionId && action === USER_GROUP_ACTIONS.MEMBERS}
        />
      </HasSomeRights>
      <Table
        resource={USER_GROUPS_RESOURCE}
        url={apiRoutes.userGroups(organisationId)}
        noResultsMessage={t('noUserGroupsFound')}
        noUserSettings
        resizable
      >
        <Actions.Link
          width="2.5rem"
          align="center"
          icon="eye"
          url={`${urlRoot}/user-groups/${USER_GROUP_ACTIONS.EDIT}/:id`}
        />
        <Params.String name="name" label="name" width="16rem" minWidth="16rem" grow />
        {!forbidManageBecauseSystemOrg && (
          <Menu width="3.5rem" align="center">
            <Actions.Link
              hasSomeRights={USER_GROUP_UPDATE_RIGHTS}
              label="edit"
              icon="edit"
              url={`${urlRoot}/user-groups/${USER_GROUP_ACTIONS.EDIT}/:id`}
            />
            <Actions.Link
              hasSomeRights={USER_GROUP_UPDATE_RIGHTS}
              label="manageMembers"
              icon="users"
              url={`${urlRoot}/user-groups/${USER_GROUP_ACTIONS.MEMBERS}/:id`}
            />
            <Actions.Delete
              hasSomeRights={USER_GROUP_DELETE_RIGHTS}
              url={`${apiRoutes.userGroups(organisationId)}/:id`}
              confirmMessage={t('areYouSureRemoveUserGroup')}
            />
          </Menu>
        )}
      </Table>
    </>
  );
};

UserGroupsOverview.propTypes = {
  organisationId: PropTypes.string.isRequired,
  urlRoot: PropTypes.string.isRequired,
  forbidManageBecauseSystemOrg: PropTypes.bool,
};

UserGroupsOverview.defaultProps = { forbidManageBecauseSystemOrg: false };

export default UserGroupsOverview;
